/* =========================================================
   ETHIOPIAN DATE & TIME UTILITIES (Amharic)
   - Ethiopian calendar (E.C.) conversion
   - Ethiopian day starts at 06:00 Gregorian (12:00 AM Ethiopian)
   - Weekday & month names in Amharic
========================================================= */

const ETHIOPIAN_MONTHS_AM = [
  'መስከረም',   // 1  Meskerem
  'ጥቅምት',    // 2  Tikimt
  'ኅዳር',     // 3  Hidar
  'ታኅሣሥ',    // 4  Tahsas
  'ጥር',      // 5  Tir
  'የካቲት',    // 6  Yekatit
  'መጋቢት',    // 7  Megabit
  'ሚያዝያ',    // 8  Miazia
  'ግንቦት',    // 9  Ginbot
  'ሰኔ',      // 10 Sene
  'ሐምሌ',     // 11 Hamle
  'ነሐሴ',     // 12 Nehase
  'ጳጉሜን',    // 13 Pagume
];

const WEEKDAYS_AM = [
  'እሑድ',     // Sunday
  'ሰኞ',      // Monday
  'ማክሰኞ',    // Tuesday
  'ረቡዕ',     // Wednesday
  'ሐሙስ',     // Thursday
  'ዓርብ',     // Friday
  'ቅዳሜ',     // Saturday
];

/**
 * Convert a Gregorian date to Ethiopian calendar components.
 */
function gregorianToEthiopian(date: Date): {
  year: number;
  month: number;
  day: number;
} {
  const gy = date.getFullYear();
  const gm = date.getMonth() + 1;
  const gd = date.getDate();

  let ethYear = gy - 7;

  const isGregLeap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
  const newYearDay = isGregLeap ? 12 : 11;
  const newYearMonth = 9; // September

  const beforeNewYear =
    gm < newYearMonth || (gm === newYearMonth && gd < newYearDay);

  if (beforeNewYear) {
    ethYear -= 1;
  }

  const refGy = ethYear + 7;
  const refIsLeap = (refGy % 4 === 0 && refGy % 100 !== 0) || refGy % 400 === 0;
  const refNewYearDay = refIsLeap ? 12 : 11;
  const refDate = new Date(refGy, 8, refNewYearDay);

  const diffMs = date.getTime() - refDate.getTime();
  let remaining = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (remaining < 0) {
    ethYear -= 1;
    const refGy2 = ethYear + 7;
    const refIsLeap2 =
      (refGy2 % 4 === 0 && refGy2 % 100 !== 0) || refGy2 % 400 === 0;
    const refNewYearDay2 = refIsLeap2 ? 12 : 11;
    const refDate2 = new Date(refGy2, 8, refNewYearDay2);
    remaining = Math.floor(
      (date.getTime() - refDate2.getTime()) / (1000 * 60 * 60 * 24)
    );
  }

  const isEthLeap = ethYear % 4 === 3;
  const pagumeDays = isEthLeap ? 6 : 5;

  let ethMonth = 1;
  let ethDay = 1;

  for (let m = 1; m <= 13; m++) {
    const len = m === 13 ? pagumeDays : 30;
    if (remaining < len) {
      ethMonth = m;
      ethDay = remaining + 1;
      break;
    }
    remaining -= len;
  }

  return { year: ethYear, month: ethMonth, day: ethDay };
}

/**
 * Convert Gregorian 24h time to Ethiopian 12h time.
 * Ethiopian clock: 06:00 Gregorian = 12:00 (12 ሰዓት) Ethiopian morning.
 * - 06:00 → 12:00 ጠዋት
 * - 07:00 → 01:00 ጠዋት
 * - 12:00 → 06:00 ከሰዓት
 * - 18:00 → 12:00 ማታ
 * - 19:00 → 01:00 ማታ
 * - 00:00 → 06:00 ሌሊት  (belongs to previous Ethiopian day)
 * - 05:00 → 11:00 ሌሊት
 */
function toEthiopianClock(hh: number, mm: number): {
  hour: number;
  minute: number;
  period: string;
} {
  let ethHour = hh - 6;
  let period: string;

  if (hh >= 6 && hh < 12) {
    period = 'ጠዋት'; // morning
  } else if (hh >= 12 && hh < 18) {
    period = 'ከሰዓት'; // afternoon
  } else if (hh >= 18 && hh < 24) {
    period = 'ማታ'; // evening
  } else {
    period = 'ሌሊት'; // night
  }

  if (ethHour <= 0) ethHour += 12;
  if (ethHour > 12) ethHour -= 12;

  return { hour: ethHour, minute: mm, period };
}

/**
 * Format an ISO date + 24h time into Ethiopian (Amharic) date/time.
 *
 * Ethiopian day boundary: a Gregorian time between 00:00–05:59 belongs
 * to the PREVIOUS Ethiopian day.
 *
 * Example:
 *   2026-10-16 00:00 → ዓርብ, ጥቅምት 7, 2019 ዓ.ም 12:00 ሌሊት  (still Oct 15 evening E.C.)
 *   2026-10-16 06:00 → ዓርብ, ጥቅምት 7, 2019 ዓ.ም 12:00 ጠዋት
 *   2026-10-16 16:15 → ዓርብ, ጥቅምት 7, 2019 ዓ.ም 10:15 ከሰዓት
 */
export function formatEthiopianDateTime(
  isoDate: string,
  time: string
): string {
  if (!isoDate) return '';

  const [y, m, d] = isoDate.split('-').map(Number);
  const [hh, mm] = (time || '00:00').split(':').map(Number);

  const baseDate = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
  if (isNaN(baseDate.getTime())) return '';

  // If Gregorian time is between 00:00 and 05:59,
  // the Ethiopian day is still the previous Gregorian day.
  const effectiveDate = new Date(baseDate);
  if ((hh || 0) < 6) {
    effectiveDate.setDate(effectiveDate.getDate() - 1);
  }

  const weekday = WEEKDAYS_AM[effectiveDate.getDay()];
  const { year, month, day } = gregorianToEthiopian(effectiveDate);
  const monthName = ETHIOPIAN_MONTHS_AM[month - 1] || '';

  const { hour, minute, period } = toEthiopianClock(hh || 0, mm || 0);
  const hhStr = String(hour).padStart(2, '0');
  const mmStr = String(minute).padStart(2, '0');

  return `${weekday}, ${monthName} ${day}, ${year} ዓ.ም ${hhStr}:${mmStr} ${period}`;
}